import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CheckIcon,
  Chip,
  Divider,
  InfoOutlinedIcon,
  ListPlusIcon,
  Stack,
  Text,
} from '@billda/design-system';
import { useMutation } from '@tanstack/react-query';
import { createLabourItemDefinition } from '../../../../../labour/api/labourItemsApi';

const MAX_VISIBLE_ITEMS = 5;

interface MissingLabourItemsCreatorProps {
  missingItems: string[];
  onAllResolved: () => void;
}

const MissingLabourItemsCreator: React.FC<MissingLabourItemsCreatorProps> = ({
  missingItems,
  onAllResolved,
}) => {
  const [createdItems, setCreatedItems] = useState<Set<string>>(new Set());
  const [creationErrors, setCreationErrors] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const createMutation = useMutation({
    mutationFn: (itemName: string) =>
      createLabourItemDefinition({ name: itemName }),
  });

  const remainingItems = missingItems.filter(item => !createdItems.has(item));
  const allItemsCreated =
    missingItems.length > 0 && remainingItems.length === 0;

  const handleCreateAll = async () => {
    setCreating(true);
    setCreationErrors([]);
    const newCreated = new Set(createdItems);
    const errors: string[] = [];

    for (const item of remainingItems) {
      try {
        await createMutation.mutateAsync(item);
        newCreated.add(item);
        setCreatedItems(new Set(newCreated));
      } catch {
        errors.push(`Failed to create "${item}"`);
      }
    }

    setCreationErrors(errors);
    setCreating(false);

    if (newCreated.size === missingItems.length) {
      onAllResolved();
    }
  };

  if (!missingItems.length) {
    return null;
  }

  const hasRemainingItems = remainingItems.length > 0;

  return (
    <Card
      className={`border bg-surface-default ${
        allItemsCreated
          ? 'border-border-success-subtle bg-surface-success-subtle'
          : 'border-border-info-subtle bg-surface-info-subtle'
      }`}
      data-testid="missing-labour-items-creator"
    >
      <CardContent className="p-6 pt-6">
        <Stack spacing={2}>
          {/* Header Section */}
          <div className="flex items-center gap-2">
            <InfoOutlinedIcon
              className={`size-4 ${
                allItemsCreated ? 'text-content-success' : 'text-content-info'
              }`}
              aria-hidden="true"
            />
            <Text
              variant="body1"
              className={`font-semibold ${
                allItemsCreated ? 'text-content-success' : 'text-content-info'
              }`}
              data-testid="missing-labour-items-title"
            >
              {allItemsCreated
                ? 'All labour items created'
                : 'New labour items needed'}
            </Text>
            {!allItemsCreated && (
              <Chip
                label={`${remainingItems.length} remaining`}
                size="sm"
                color="info"
                variant="outlined"
              />
            )}
          </div>

          {/* Explanatory text */}
          {hasRemainingItems && (
            <Text variant="body2" color="secondary">
              Your import references items that don&apos;t exist yet. Create
              them below so the import can proceed.
            </Text>
          )}

          {/* Error Messages */}
          {creationErrors.length > 0 && (
            <>
              <Divider />
              <Alert
                severity="error"
                data-testid="missing-labour-items-creation-errors"
              >
                <Text variant="body2" className="mb-2 font-semibold">
                  Failed to create some items:
                </Text>
                <ul className="m-0 pl-4">
                  {creationErrors.map((error, index) => (
                    <li key={index}>
                      <Text variant="body2" as="span">
                        {error}
                      </Text>
                    </li>
                  ))}
                </ul>
              </Alert>
            </>
          )}

          {/* Missing Items List */}
          {hasRemainingItems && (
            <div
              className="max-h-60 overflow-y-auto rounded-lg border border-border-default bg-surface-default"
              data-testid="missing-labour-items-chips"
              style={{ maxHeight: MAX_VISIBLE_ITEMS * 48 }}
            >
              {remainingItems.map(item => (
                <div
                  key={item}
                  className="border-b border-border-default py-2 pr-3 pl-3 last:border-b-0"
                >
                  <Text variant="body2">{item}</Text>
                </div>
              ))}
            </div>
          )}

          {/* Create All button — below the list */}
          {hasRemainingItems && (
            <div className="flex justify-end">
              <Button
                variant="neutral"
                size="sm"
                loading={creating}
                onClick={handleCreateAll}
                disabled={creating}
                className="min-w-30"
                data-testid="create-all-missing-labour-items-button"
              >
                {creating ? null : (
                  <ListPlusIcon className="size-4" aria-hidden="true" />
                )}
                {creating ? 'Creating...' : 'Create All'}
              </Button>
            </div>
          )}

          {/* Success Message */}
          {allItemsCreated && (
            <Alert
              severity="success"
              icon={<CheckIcon className="size-5" aria-hidden="true" />}
              data-testid="missing-labour-items-success"
            >
              All items created. You can now proceed.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MissingLabourItemsCreator;
